/**
 * Sistema Permessi Hub/App
 * Estende il modello esistente admin/user con controlli granulari per Hub e App
 * 
 * NOTA: Questo file mantiene backward compatibility.
 * Nuovi types in hub-config.ts e hub-permissions.ts
 */

import type { HubId as HubIdImport, AppId as AppIdImport } from './hub-config';

// Re-export per backward compatibility
export type { HubId, AppId } from './hub-config';
export type { 
  AppPermission as AppPermissionNew,
  HubPermission as HubPermissionNew,
  UserPermissions as UserPermissionsNew,
  AppPermissionCalculated,
  HubPermissionCalculated
} from './hub-permissions';

// Types legacy (mantenuti per compatibilità)
export type HubIdLegacy = 'business' | 'dipendenze' | 'applicativi';
export type AppIdLegacy = 'dashboard' | 'personale' | 'portafoglio' | 'gestione-assistiti' | 'gestione-strutture' | 'documentazione' | 'configurazione';

/**
 * Permessi per una singola app dentro un hub
 */
export interface AppPermission {
  canAccess: boolean;
  filters?: string[]; // Future: filtri per centro di costo
  crud?: {
    create?: boolean;
    read?: boolean;
    update?: boolean;
    delete?: boolean;
  };
}

/**
 * Permessi per un hub completo
 */
export interface HubPermission {
  hubId: HubIdImport;
  canView: boolean; // Può vedere il hub nella sidebar/home
  apps: {
    [key in AppIdImport]?: AppPermission;
  };
}

/**
 * Permessi completi utente
 */
export interface UserPermissions {
  role: 'admin' | 'user';
  hubs: HubPermission[];
}

/**
 * Helper per verificare permessi
 */
export class PermissionsHelper {
  private permissions: UserPermissions;

  constructor(permissions: UserPermissions) {
    this.permissions = permissions;
  }

  /**
   * Verifica se l'utente può vedere un hub
   */
  canViewHub(hubId: HubIdImport): boolean {
    const hub = this.permissions.hubs.find(h => h.hubId === hubId);
    return hub?.canView ?? false;
  }

  /**
   * Verifica se l'utente può accedere a un'app specifica
   */
  canAccessApp(hubId: HubIdImport, appId: AppIdImport): boolean {
    const hub = this.permissions.hubs.find(h => h.hubId === hubId);
    if (!hub) {
      return false;
    }

    return hub.apps[appId]?.canAccess ?? false;
  }

  /**
   * Ottieni tutti gli hub visibili
   */
  getVisibleHubs(): HubIdImport[] {
    return this.permissions.hubs
      .filter(h => h.canView)
      .map(h => h.hubId);
  }

  /**
   * Ottieni tutte le app accessibili per un hub
   */
  getAccessibleApps(hubId: HubIdImport): AppIdImport[] {
    const hub = this.permissions.hubs.find(h => h.hubId === hubId);
    if (!hub) {
      return [];
    }

    return Object.entries(hub.apps)
      .filter(([_, perm]) => perm?.canAccess)
      .map(([appId]) => appId as AppIdImport);
  }

  /**
   * Permessi CRUD
   */
  private getAppCrud(hubId: HubIdImport, appId: AppIdImport) {
    const hub = this.permissions.hubs.find(h => h.hubId === hubId);
    if (!hub) return { create: false, read: false, update: false, delete: false };
    const app = hub.apps[appId];
    if (!app?.canAccess) return { create: false, read: false, update: false, delete: false };
    return app.crud || { create: false, read: true, update: false, delete: false };
  }

  canCreate(hubId: HubIdImport, appId: AppIdImport): boolean {
    return this.getAppCrud(hubId, appId).create ?? false;
  }

  canRead(hubId: HubIdImport, appId: AppIdImport): boolean {
    return this.getAppCrud(hubId, appId).read ?? false;
  }

  canUpdate(hubId: HubIdImport, appId: AppIdImport): boolean {
    return this.getAppCrud(hubId, appId).update ?? false;
  }

  canDelete(hubId: HubIdImport, appId: AppIdImport): boolean {
    return this.getAppCrud(hubId, appId).delete ?? false;
  }
}

/**
 * DEPRECATED: Removed hardcoded permission calculation
 * 
 * All permissions are now managed via Admin Hub:
 * - Base access for all @polo9.org users
 * - Specific scopes assigned per-user in system_config/permissions/users/{uid}
 * - No more group-based hardcoded logic
 * 
 * Use permissionsCalculator.ts for dynamic permission calculation based on backend data
 */
